import {
  AppConfig,
  useAppConstantsContext,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { AIFamilyPhotosDashboardBanner } from '@ltvco/refresh-lib/fmt';
import { useMonitorUpsell } from '@ltvco/refresh-lib/payments';
import { Text, Grid, Box } from '@ltvco/refresh-lib/theme';
import {
  capitalizeEachWord,
  isUserInPhoneOnlyTest,
  useScreenSize,
} from '@ltvco/refresh-lib/utils';
import { useAccount } from '@ltvco/refresh-lib/v1';
import {
  FeatureBlockedModal,
  FeatureCard,
  MonitoringPlusUpsellCard,
  useFeatureBlockedModal,
  useSriracha,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MobileDownloadSnackbar } from 'components/MobileDownloadSnackbar/MobileDownloadSnackbar';
import {
  FamilyThree,
  UnclaimedMoney,
  DarkWebScan,
  Monitoring,
  MemberPerks,
  FraudScan,
  Identity,
  TheftInsurance,
} from 'icons';

import { SearchHub } from '../SearchHub/SearchHub';

import DashboardDigitialFoorprint from './components/DashboardDigitialFoorprint';

export function DashboardPage() {
  useEffect(() => {
    document.title = 'Dashboard - BeenVerified';
  }, []);
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();
  const { data: account } = useAccount(true);
  const {
    isFeatureBlockedModalOpen,
    closeFeatureBlockedModal,
    openFeatureBlockedModal,
  } = useFeatureBlockedModal();
  const [searchType, setSearchType] = useState('');
  const [isInSingleReportVariation, setIsInSingleReportVariation] =
    useState(false);
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const {
    data: { brandName },
  } = useAppConstantsContext();
  const { shouldShowMonitoringAddonUpsell } = useMonitorUpsell();
  const { trackEventGA4 } = useContext(AppConfig);
  const { sendNotification, getSuggestedReportNotificationSources } =
    useSriracha();

  const { data: notificationSources } = getSuggestedReportNotificationSources;

  const inUCMAddonVariation = useFeatureIsOn('RTRN-1526');
  const hasPurchasedUCMAddon =
    account?.account?.subscription_info?.one_time_ucm_purchased;
  const shouldDisplayUcmAddonModal =
    !!account && inUCMAddonVariation && !hasPurchasedUCMAddon;

  useEffect(() => {
    if (!account) return;
    if (!notificationSources) return;

    if (
      account?.account.user_info.user_code &&
      notificationSources?.suggested_report_sources
    ) {
      const sources = notificationSources?.suggested_report_sources;

      const newFeatureNotificationExists = sources?.find(
        (item: string) => item === 'new_feature'
      );

      if (!newFeatureNotificationExists) {
        sendNotification.mutate({
          'notification[user_code]': account?.account.user_info.user_code,
          'notification[notification_type]': 'new_feature',
          'notification[data][brand]': brandName,
          'notification[data][title]': 'New Feature: AI Family Photos',
          'notification[data][body]':
            'Create unique family photos that stretch the imagination and creativity with AI.',
          'notification[data][feature]': 'ai_family_photos_get_started',
          'notification[data][link]': '/dashboard/ai-family-photos',
        });
      }
    }
  }, [
    account?.account.user_info.user_code,
    notificationSources?.suggested_report_sources,
  ]);

  const {
    singleReportVariation,
    isInInternationalOrVinVariations,
    isBlocked,
    isHidden,
    phoneOnlyVariation,
  } = useSubscriptionAccessConfigContext();
  const { phoneOnlyPlanNames } = useAppConstantsContext();
  const isPhoneOnlyVariation =
    isUserInPhoneOnlyTest(account, phoneOnlyPlanNames) || phoneOnlyVariation;

  useEffect(() => {
    if (!account) return;
    setIsInSingleReportVariation(singleReportVariation);
  }, [account]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isFallFromCTAEmail = urlParams.get('premiumData');

    if (isInSingleReportVariation && isFallFromCTAEmail === 'true') {
      openFeatureBlockedModal();
    }
  }, [isInSingleReportVariation]);

  const featureAction = (feature: string) => {
    if (feature === 'unclaimed money' && shouldDisplayUcmAddonModal) {
      trackEventGA4({
        event_name: 'engagement_click',
        type: 'cta_dashboard_promo_card',
        subtype: 'ucm_upsell',
      });
    }

    setSearchType(capitalizeEachWord(feature));
    openFeatureBlockedModal();
  };

  const allFeatures = [
    {
      title: 'Family Tree',
      description:
        'Start building your family tree! Organize your information now',
      icon: <FamilyThree />,
      key: 'ancestry',
      action: () =>
        isBlocked('ancestry')
          ? featureAction('family-tree')
          : navigate('/family-tree'),
    },
    {
      title: 'Unclaimed Money',
      description: 'Find out what money you may be owed',
      icon: <UnclaimedMoney />,
      key: 'unclaimed_money',
      action: () =>
        isBlocked('unclaimed_money') || shouldDisplayUcmAddonModal
          ? featureAction('unclaimed money')
          : navigate('/dashboard/unclaimed-money'),
    },
    {
      title: 'Dark Web Scan',
      description: 'Check if your email was potentially leaked',
      icon: <DarkWebScan />,
      key: 'dark_web',
      action: () =>
        isBlocked('dark_web')
          ? featureAction('dark web')
          : navigate('/dashboard/dark-web'),
    },
    {
      title: 'Identity & Credit Alerts',
      description: 'Monitor potential identity and credit record activity',
      icon: <Identity />,
      action: () => navigate('/dashboard/id-monitor?escalated=true'),
      key: 'identity_alerts', // Used for phone-only variation gating 02/04/2026 LC
    },
    {
      title: 'Monitor your reports',
      description: 'Receive updates if we detect changes',
      icon: <Monitoring />,
      action: () => navigate('/dashboard/monitoring'),
      key: 'monitor_reports', // Used for phone-only variation gating 02/04/2026 LC
    },
    {
      title: 'Member Perks',
      description:
        'Savings may be available through gift cards, discounts, and more',
      icon: <MemberPerks />,
      action: () => navigate('/dashboard/rewards'),
    },
    {
      title: '$1M ID Theft Insurance',
      description:
        'Protect your identity with identity theft insurance and restoration services',
      icon: <TheftInsurance />,
      action: () => navigate('/dashboard/id-monitor?escalated=true'),
      key: 'id_theft_insurance', // Used for phone-only variation gating
    },
    {
      title: 'FraudScan',
      description:
        'Check phone numbers and emails for threats and fraudulent info',
      icon: <FraudScan />,
      key: 'fraud_scan',
      action: () =>
        isBlocked('fraud_scan')
          ? featureAction('fraud scan')
          : navigate('/dashboard/fraud'),
    },
  ];

  // Features that should be hidden for phone-only variation users 02/04/2026 LC
  const phoneOnlyGatedFeatures = [
    'identity_alerts',
    'monitor_reports',
    'id_theft_insurance',
  ];

  const features = allFeatures.filter((feature) => {
    // Filter out hidden features (plan-based access control) 02/04/2026 LC
    if (feature.key && isHidden(feature.key)) {
      return false;
    }
    // Filter out identity protection features for phone-only variation users 02/04/2026 LC
    if (
      feature.key &&
      phoneOnlyGatedFeatures.includes(feature.key) &&
      isPhoneOnlyVariation
    ) {
      return false;
    }
    return true;
  });

  const handleMonitoringUpsellDashboardClick = (): void => {
    trackEventGA4({
      event_name: 'engagement_click',
      type: 'cta_dashboard',
      section_name: 'monitoring_upsell',
    });
    setSearchType('Report Monitoring');
    setPaymentShowModalNumber(0);
    openFeatureBlockedModal();
  };

  const handleMonitoringUnlockClick = (): void => {
    if (searchType === 'Report Monitoring') {
      trackEventGA4({
        event_name: 'engagement_click',
        type: 'confirmation_unlock',
        section_name: 'monitoring_upsell',
      });
    }
  };

  const handleMonitoringLegalAccepted = (): void => {
    if (searchType === 'Report Monitoring') {
      trackEventGA4({
        event_name: 'engagement_click',
        type: 'last_step_unlock',
        section_name: 'monitoring_upsell',
      });
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      rowGap={2.5}
      paddingX={{ xs: 2.5, sm: 0 }}
    >
      <SearchHub shouldDisplayUcmAddonModal={shouldDisplayUcmAddonModal} />
      <DashboardDigitialFoorprint />
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
        alignItems="stretch"
        sx={{ width: '100%' }}
      >
        {shouldShowMonitoringAddonUpsell ? (
          <MonitoringPlusUpsellCard
            sx={{ flexShrink: 0 }}
            onUpgradeClick={handleMonitoringUpsellDashboardClick}
          />
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <AIFamilyPhotosDashboardBanner />
        </Box>
      </Box>

      {isInInternationalOrVinVariations ? null : (
        <Grid container direction="column">
          <Grid>
            <Text variant="h3" mb={3} mt={2}>
              Explore New Features
            </Text>
          </Grid>
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {features.map((feature) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feature.title}>
                <FeatureCard
                  title={feature.title}
                  description={feature.description}
                  icon={feature.icon}
                  action={feature.action}
                  width={'100%'}
                  height={120}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType={searchType}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
        onUnlockClick={handleMonitoringUnlockClick}
        onLegalAccepted={handleMonitoringLegalAccepted}
      />
      <MobileDownloadSnackbar />
    </Box>
  );
}
